import React, {useEffect} from 'react';
import {connect} from "react-redux";
import {Link} from "react-router-dom";
import {withTranslation} from "react-i18next";
import Footer from "../core/layout/footer/footer";
import './technologyPartnership.css';

/* ─── Section data ───────────────────────────── */
const reasons = [
    {icon: 'fa-rocket',            delay: '',   title: 'Time-to-Market Acceleration',  desc: 'For time-sensitive projects, an external partner can expedite development, ensuring a quicker launch and a competitive edge in the market.'},
    {icon: 'fa-expand-arrows-alt', delay: 'd1', title: 'Scalability and Expansion',     desc: 'When scaling your business, an external partner can facilitate rapid expansion by quickly developing and launching new MVP iterations.'},
    {icon: 'fa-brain',             delay: 'd2', title: 'Expertise and Specialization',  desc: 'When your team lacks specific technical skills or experience, an external partner brings specialized expertise to ensure a robust and high-quality MVP.'},
    {icon: 'fa-coins',             delay: 'd3', title: 'Cost-Efficiency',               desc: 'Collaborating with an external partner can often be more cost-effective than building an in-house team and infrastructure from scratch.'},
    {icon: 'fa-shield-alt',        delay: 'd4', title: 'Risk Mitigation',               desc: "Startups and businesses can reduce risks by leveraging the partner's proven track record and experience, minimizing potential pitfalls."},
    {icon: 'fa-cogs',              delay: 'd5', title: 'Resource Optimization',         desc: 'Partnering externally lets you focus on core business functions while experts handle the intricacies of MVP development, optimizing resource allocation.'},
    {icon: 'fa-lightbulb',         delay: '',   title: 'Innovation',                    desc: 'Leveraging external perspectives infuses fresh ideas and innovative approaches, enhancing the uniqueness and appeal of your MVP.'},
];

const whyUsItems = [
    {icon: 'fa-lightbulb',    delay: '',   title: 'Innovation That Accelerates Your Vision', desc: 'Our creative approach drives inventive solutions tailored to your unique product concept. Our agile development ensures swift MVP realization, giving you a competitive advantage.'},
    {icon: 'fa-check-double', delay: 'd1', title: 'Reliability',                              desc: "Our proven track record reflects a steadfast commitment to delivering results on time and with excellence. We don't disappear after the demo."},
    {icon: 'fa-comments',     delay: 'd2', title: 'Integrity and Transparency',               desc: 'We prioritize open communication and ethical practices, fostering a trusting and collaborative partnership throughout the engagement.'},
    {icon: 'fa-sync-alt',     delay: 'd3', title: 'Flexibility',                              desc: 'Our adaptable mindset empowers us to flex with your changing needs, ensuring your product\'s success story regardless of how the landscape shifts.'},
];

const areas = [
    {delay: '',   title: 'Build MVP',             desc: 'Launch fast, gather feedback, and conquer. We help you get to market quickly with a solid foundation built to iterate.'},
    {delay: 'd1', title: 'Build Add-on Features', desc: 'Elevate, engage, succeed. We extend your existing product with new capabilities that delight users and drive retention.'},
    {delay: 'd2', title: 'Scale the Product',     desc: 'Unleash exponential growth, free from IT constraints. We architect and build the systems that support your next stage of growth.'},
];

const benefits = [
    {delay: '',   title: 'Lower CapEx',           desc: 'Cash is king for businesses. With our engagement, our partners can lower their technology expenditure without compromising business growth plans.'},
    {delay: 'd1', title: 'Competitive Advantage', desc: 'With our capabilities and IP assets, our partners can gain a significant competitive advantage in their respective markets.'},
    {delay: 'd2', title: 'Faster Time-to-Market', desc: 'Our pre-built frameworks and deep experience across domains mean you ship faster than competitors building from scratch.'},
    {delay: 'd3', title: 'Senior-Level Access',   desc: 'No account managers or junior handoffs. You work directly with the engineers and architects making the technical decisions.'},
    {delay: 'd4', title: 'Shared Risk & Reward',  desc: 'We operate as a true partner — invested in your success, not just in completing a statement of work.'},
    {delay: 'd5', title: 'Long-term Relationship', desc: 'One project often reveals the next. We grow your technology capability with you over time, not just for a single engagement.'},
];

/* ─── Component ──────────────────────────────── */
function TechnologyPartnership() {

    useEffect(() => {
        window.scrollTo(0, 0);

        const io = new IntersectionObserver(entries => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    e.target.classList.add('visible');
                    io.unobserve(e.target);
                }
            });
        }, {threshold: 0.08});

        document.querySelectorAll('.tp-fade-up').forEach(el => io.observe(el));
        return () => io.disconnect();
    }, []);

    /* ── Section 1: Hero ─────────────────────── */
    const renderHero = () => (
        <section className="tp-page-hero">
            <div className="tp-page-hero-inner">
                <div className="tp-page-eyebrow">
                    <i className="fas fa-handshake"></i> Technology Partnership
                </div>
                <h1>Your Partner in <span>Product Development</span></h1>
                <p className="tp-page-hero-desc">
                    Why and when to choose an external tech partner for your product — and what makes the
                    difference between a vendor and a true partner.
                </p>
            </div>
        </section>
    );

    /* ── Section 2: Why & When ───────────────── */
    const renderWhyWhen = () => (
        <section className="tp-content">
            <div className="tp-wrap">
                <div className="tp-fade-up">
                    <div className="tp-section-tag">Why &amp; When</div>
                    <h2 className="tp-s-title">When to choose an <span>external tech partner</span></h2>
                    <p className="tp-s-sub">
                        An external technology partner is the right choice in several distinct situations.
                        Here's when it makes the most sense.
                    </p>
                </div>
                <div className="tp-reasons-grid">
                    {reasons.map(({icon, delay, title, desc}) => (
                        <div key={title} className={`tp-reason-card tp-fade-up${delay ? ' ' + delay : ''}`}>
                            <div className="tp-reason-icon"><i className={`fas ${icon}`}></i></div>
                            <h3>{title}</h3>
                            <p>{desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    /* ── Section 3: Why Us ───────────────────── */
    const renderWhyUs = () => (
        <section className="tp-content tp-why-us">
            <div className="tp-wrap">
                <div className="tp-fade-up">
                    <div className="tp-section-tag">Why Us</div>
                    <h2 className="tp-s-title">Why choose <span>DeccanLogic</span> as your tech partner?</h2>
                    <p className="tp-s-sub">
                        We bring more than technical capability — we bring a partnership mindset,
                        senior-level engagement, and an honest approach to every engagement.
                    </p>
                </div>
                <div className="tp-why-us-grid">
                    {whyUsItems.map(({icon, delay, title, desc}) => (
                        <div key={title} className={`tp-wu-card tp-fade-up${delay ? ' ' + delay : ''}`}>
                            <div className="tp-wu-icon"><i className={`fas ${icon}`}></i></div>
                            <div>
                                <h3>{title}</h3>
                                <p>{desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    /* ── Section 4: Contribution Areas ──────── */
    const renderAreas = () => (
        <section className="tp-content">
            <div className="tp-wrap">
                <div className="tp-fade-up">
                    <div className="tp-section-tag">Contribution Areas</div>
                    <h2 className="tp-s-title">Areas where <span>we can contribute</span></h2>
                    <p className="tp-s-sub">
                        From initial build to full-scale growth — we engage across the full product lifecycle.
                    </p>
                </div>
                <div className="tp-areas-grid">
                    {areas.map(({delay, title, desc}) => (
                        <div key={title} className={`tp-area-card tp-fade-up${delay ? ' ' + delay : ''}`}>
                            <h3>{title}</h3>
                            <p>{desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    /* ── Section 5: Partnership Benefits ─────── */
    const renderBenefits = () => (
        <section className="tp-content tp-why-us">
            <div className="tp-wrap">
                <div className="tp-fade-up">
                    <div className="tp-section-tag">Partnership Benefits</div>
                    <h2 className="tp-s-title">What you gain from <span>partnering with us</span></h2>
                    <p className="tp-s-sub">
                        Our partnership model is designed to give you enterprise-grade technology capability
                        without enterprise overhead.
                    </p>
                </div>
                <div className="tp-benefits-grid">
                    {benefits.map(({delay, title, desc}) => (
                        <div key={title} className={`tp-benefit-card tp-fade-up${delay ? ' ' + delay : ''}`}>
                            <h3>{title}</h3>
                            <p>{desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    /* ── Section 6: CTA Banner ───────────────── */
    const renderCTA = () => (
        <section className="tp-cta-banner">
            <div className="tp-cta-inner">
                <div className="tp-fade-up">
                    <h2>Ready to find your tech partner?</h2>
                    <p>
                        Tell us about what you're building. We'll tell you honestly whether we're the right
                        fit — and if we are, what the partnership would look like.
                    </p>
                </div>
                <div className="tp-cta-actions tp-fade-up d2">
                    <Link to="/contact-us" className="dl-btn dl-btn-white">
                        <i className="fas fa-envelope"></i> info@deccanlogic.com
                    </Link>
                </div>
            </div>
        </section>
    );

    /* ── Render ──────────────────────────────── */
    return (
        <div className="w-100">
            {renderHero()}
            {renderWhyWhen()}
            {renderWhyUs()}
            {renderAreas()}
            {renderBenefits()}
            {renderCTA()}
            <Footer/>
        </div>
    );
}

TechnologyPartnership.propTypes = {};

TechnologyPartnership.defaultProps = {};

const mapStateToProps = (state) => ({
    isMobileView: state.appState.deviceInfo.isMobileView,
    isLGView: state.appState.deviceInfo.isLGView,
    isXLView: state.appState.deviceInfo.isXLView,
    isXSView: state.appState.deviceInfo.isXSView,
    isSMView: state.appState.deviceInfo.isSMView,
});

export default connect(mapStateToProps, {})(withTranslation('translations')(TechnologyPartnership));
